// js/router.js (VERSÃO PREMIUM COM GLOB IMPORTS E LIMPEZA DE LISTENERS)

class EventTracker {
    constructor() {
        this.listeners = [];
    }

    /**
     * Adiciona um event listener a um elemento e rastreia para remoção automática.
     * @param {EventTarget} target - O alvo do evento (window, document, element).
     * @param {string} type - Tipo de evento (ex: 'click', 'resize').
     * @param {function} listener - O callback do evento.
     * @param {object|boolean} [options] - Opções de escuta.
     */
    addEventListener(target, type, listener, options) {
        if (!target) return;
        target.addEventListener(type, listener, options);
        this.listeners.push({ target, type, listener, options });
    }

    /**
     * Remove todos os event listeners registrados por este rastreador.
     */
    clearAll() {
        for (const { target, type, listener, options } of this.listeners) {
            try {
                target.removeEventListener(type, listener, options);
            } catch (e) {
                console.error('[EventTracker] Falha ao remover listener:', e);
            }
        }
        this.listeners = [];
    }
}

// Vite glob import para pré-compilar e mapear todos os arquivos JS das páginas de forma dinâmica
const pageModules = import.meta.glob('./pages/**/*.js');

export class Router {
    #routes;
    #appRoot;
    #currentModule = null;
    #currentTracker = null;

    constructor(routes, appRootSelector = '#app-root') {
        this.#routes = routes;
        this.#appRoot = document.querySelector(appRootSelector);
        if (!this.#appRoot) {
            throw new Error(`Router Error: O elemento raiz '${appRootSelector}' não foi encontrado no DOM.`);
        }
    }

    /**
     * Inicializa o roteador, adicionando listeners para a History API e carregamento da página.
     */
    init() {
        window.addEventListener('popstate', () => this.handleRouteChange());
        window.addEventListener('hashchange', () => {
            const hash = window.location.hash;
            if (hash.startsWith('#/')) {
                const path = hash.substring(1);
                // Converte a hash antiga para a History API e limpa a hash da URL
                window.history.replaceState({}, '', path);
                window.location.hash = '';
                this.handleRouteChange();
            }
        });
        window.addEventListener('load', () => this.handleRouteChange());
        console.log('[Router] Roteador aprimorado inicializado com History API.');
    }

    /**
     * Navega para um caminho específico usando a History API.
     * @param {string} path - O caminho de destino.
     */
    navigate(path) {
        window.history.pushState({}, '', path);
        this.handleRouteChange();
    }

    /**
     * Encontra a rota correspondente e extrai parâmetros dinâmicos da URL.
     * @param {string} currentPath - O caminho limpo da URL atual.
     * @returns {{route: object, params: object} | null} O objeto da rota e seus parâmetros.
     */
    #matchRoute(currentPath) {
        for (const route of this.#routes) {
            const routeParts = route.path.split('/');
            const pathParts = currentPath.split('/');

            if (routeParts.length !== pathParts.length) {
                continue;
            }

            const params = {};
            const isMatch = routeParts.every((routePart, i) => {
                if (routePart.startsWith(':')) {
                    const paramName = routePart.substring(1);
                    params[paramName] = pathParts[i];
                    return true;
                }
                return routePart === pathParts[i];
            });

            if (isMatch) {
                return { route, params };
            }
        }
        return null;
    }

    /**
     * Lida com a mudança de rota, carregando o novo conteúdo e rolando para o topo.
     */
    async handleRouteChange() {
        // 1. Limpa o módulo anterior e limpa os event listeners para evitar memory leaks
        if (this.#currentModule && typeof this.#currentModule.destroy === 'function') {
            this.#currentModule.destroy();
            console.log(`[Router] Módulo anterior destruído.`);
        }
        this.#currentModule = null;

        if (this.#currentTracker) {
            this.#currentTracker.clearAll();
            console.log(`[Router] Event listeners da página anterior limpos.`);
        }
        this.#currentTracker = new EventTracker();
        
        const path = window.location.pathname || '/';
        const queryString = window.location.search.slice(1);
        const queryParams = new URLSearchParams(queryString || '');
        const queryParamsObject = Object.fromEntries(queryParams.entries());

        // Encontra a rota correspondente ou a de 404
        const match = this.#matchRoute(path);
        const { route, params } = match || { route: this.#routes.find(r => r.path === '/404'), params: {} };
        
        if (!route) {
            console.error(`[Router] Rota não encontrada para '${path}' e nenhuma rota 404 definida.`);
            this.#appRoot.innerHTML = '<h1>404 - Página Não Encontrada</h1>';
            return;
        }

        this.#appRoot.innerHTML = '<div class="loading-spinner"></div>';

        try {
            // 2. Carrega o template HTML (suporta injeção direta de raw string do Vite ou fetch dinâmico)
            if (typeof route.template === 'string' && route.template.trim().startsWith('<')) {
                this.#appRoot.innerHTML = route.template;
            } else {
                const cacheBuster = `?v=${new Date().getTime()}`;
                const response = await fetch(route.template + cacheBuster);
                if (!response.ok) throw new Error(`Template não encontrado: ${route.template}`);
                const html = await response.text();
                this.#appRoot.innerHTML = html;
            }

            // 3. Carrega e executa o script JS da página de forma compatível com Vite glob imports
            if (route.js) {
                // Garante que o caminho corresponda à chave do glob (ex: './pages/home.js')
                const pathKey = route.js;
                const importFunc = pageModules[pathKey];

                if (!importFunc) {
                    throw new Error(`Módulo de página não encontrado no Vite Glob: ${pathKey}`);
                }

                const { default: PageModule } = await importFunc();
                this.#currentModule = PageModule;
                if (PageModule && typeof PageModule.init === 'function') {
                    PageModule.init(params, queryParamsObject, this.#currentTracker);
                    console.log(`[Router] Módulo para '${route.path}' inicializado.`);
                }
            }

            // 4. Atualiza os links de navegação
            this.updateNavLinks(path);

            // 5. Atualiza o título do documento (SEO)
            if (route.title) {
                document.title = `${route.title} | Runeweave`;
            } else {
                document.title = 'Runeweave | O Portal de Eldraem';
            }

            // 6. Rola a janela para o topo
            window.scrollTo(0, 0);
            
        } catch (error) {
            console.error(`[Router] Erro ao carregar a rota '${path}':`, error);
            this.#appRoot.innerHTML = '<h1>Erro ao carregar a página.</h1><p>Verifique o console para mais detalhes.</p>';
        }
    }
    
    /**
     * Atualiza a classe 'active' nos links de navegação do header.
     * @param {string} currentPath - O caminho da rota atual.
     */
    updateNavLinks(currentPath) {
        const navLinks = document.querySelectorAll('header nav a');
        navLinks.forEach(link => {
            // No history routing, comparamos o pathname do link
            let linkPath = link.getAttribute('href');
            if (linkPath && linkPath.startsWith('#/')) {
                linkPath = linkPath.substring(1);
            }
            
            if (linkPath === currentPath || (linkPath === '/comunidade' && currentPath.startsWith('/comunidade/'))) {
                link.classList.add('active');
            } else {
                link.classList.remove('active');
            }
        });
    }
}